import _objectSpread from '@babel/runtime/helpers/objectSpread2';
import _objectWithoutProperties from '@babel/runtime/helpers/objectWithoutProperties';
import * as React from 'react';
import styled from 'styled-components';
import { useTranslations } from '../../hooks/use_translations.js';
import { getDropShadowByElevation } from '../../styles/elevation.js';
import { spacing1x, spacing3x, spacing2x } from '../../styles/spacing.js';
import { useThemeContext } from '../../styles/themes/themes.js';
import { getAdornment } from '../../utils/adornment.js';
import { isEmpty } from '../../utils/lang.js';
import { IconHost } from '../IconHost/IconHost.js';
import { IconClose } from '../Icons/IconClose.js';
import { IconDrag } from '../Icons/IconDrag.js';
import { LinkButton } from '../LinkButton/LinkButton.js';
import { AdornmentContainer } from './AdornmentContainer.js';
import { jsx, jsxs } from 'react/jsx-runtime';

var _excluded = ["dragHandleProps", "iconHostProps", "isDraggable", "linkProps", "onClose", "startAdornment", "title"];
var ContentChipContainer = styled.article.withConfig({
  displayName: "ContentChipBase__ContentChipContainer",
  componentId: "sc-1p2ozy3-0"
})(["&&{display:flex;align-items:center;justify-content:space-between;background-color:", ";border-style:solid;border-color:", ";border-width:1px;border-radius:", ";height:52px;padding:0 ", ";}&&:hover,&&:focus,&&:focus-within{box-shadow:", ";outline:0;}"], function (_ref) {
  var theme = _ref.theme;
  return theme.neutralColors.white;
}, function (_ref2) {
  var theme = _ref2.theme;
  return theme.neutralColors.silver;
}, spacing1x, spacing3x, getDropShadowByElevation(2));
var StyledAnchor = styled.a.withConfig({
  displayName: "ContentChipBase__StyledAnchor",
  componentId: "sc-1p2ozy3-1"
})(["&&{color:", ";cursor:pointer;}&&:hover,&&:focus,&&:active{color:", ";}"], function (_ref3) {
  var theme = _ref3.theme;
  return theme.neutralColors.charcoal;
}, function (_ref4) {
  var theme = _ref4.theme;
  return theme.neutralColors.charcoal;
});
var Content = styled.div.withConfig({
  displayName: "ContentChipBase__Content",
  componentId: "sc-1p2ozy3-2"
})(["&&{display:-webkit-box;-webkit-line-clamp:2;-webkit-box-orient:vertical;overflow:hidden;line-height:18px;}"]);
var TitleContainer = styled.div.withConfig({
  displayName: "ContentChipBase__TitleContainer",
  componentId: "sc-1p2ozy3-3"
})(["&&{display:flex;margin-inline-start:", ";align-items:center;}"], spacing1x);
var StyledDragIcon = styled(IconDrag).withConfig({
  displayName: "ContentChipBase__StyledDragIcon",
  componentId: "sc-1p2ozy3-4"
})(["&&{margin-inline-end:", ";cursor:grab;}"], spacing2x);
var ContentContainer = styled.div.withConfig({
  displayName: "ContentChipBase__ContentContainer",
  componentId: "sc-1p2ozy3-5"
})(["&&{display:flex;align-items:center;}"]);
var CloseIconContainer = styled.div.withConfig({
  displayName: "ContentChipBase__CloseIconContainer",
  componentId: "sc-1p2ozy3-6"
})(["&&{padding-inline-start:", ";}"], spacing1x);
var StyledIconHost = styled(IconHost).withConfig({
  displayName: "ContentChipBase__StyledIconHost",
  componentId: "sc-1p2ozy3-7"
})(["&&{margin-inline-end:", ";}"], spacing1x);
var ContentChipBase = /*#__PURE__*/React.forwardRef(function ContentChipBase2(props, ref) {
  var _props$dragHandleProp = props.dragHandleProps,
    dragHandleProps = _props$dragHandleProp === void 0 ? {} : _props$dragHandleProp,
    iconHostProps = props.iconHostProps,
    isDraggable = props.isDraggable,
    _props$linkProps = props.linkProps,
    linkProps = _props$linkProps === void 0 ? {} : _props$linkProps,
    onClose = props.onClose,
    startAdornment = props.startAdornment,
    title = props.title,
    rest = _objectWithoutProperties(props, _excluded);
  var _useThemeContext = useThemeContext(),
    localization = _useThemeContext.localization;
  var _useTranslations = useTranslations(localization === null || localization === void 0 ? void 0 : localization.translations),
    t = _useTranslations.t;
  var isDragHandlePropsEmpty = isEmpty(dragHandleProps);
  var focusProps = isDragHandlePropsEmpty ? {
    tabIndex: 0
  } : {};
  var startIcon = null;
  if (startAdornment) {
    startIcon = /*#__PURE__*/jsx(AdornmentContainer, {
      children: getAdornment(startAdornment, {
        size: 20
      })
    });
  } else if (!isEmpty(iconHostProps)) {
    startIcon = /*#__PURE__*/jsx(StyledIconHost, _objectSpread({
      size: 20
    }, iconHostProps));
  }
  return /*#__PURE__*/jsxs(ContentChipContainer, _objectSpread(_objectSpread(_objectSpread({
    ref: ref
  }, focusProps), rest), {}, {
    children: [/*#__PURE__*/jsxs(ContentContainer, {
      children: [(isDraggable || !isDragHandlePropsEmpty) && /*#__PURE__*/jsx("div", _objectSpread(_objectSpread({}, dragHandleProps), {}, {
        children: /*#__PURE__*/jsx(StyledDragIcon, {
          size: 20
        })
      })), /*#__PURE__*/jsx(TitleContainer, {
        children: /*#__PURE__*/jsxs(Content, {
          children: [startIcon, /*#__PURE__*/jsx(StyledAnchor, _objectSpread(_objectSpread({
            tabIndex: 0
          }, linkProps), {}, {
            children: title
          }))]
        })
      })]
    }), onClose && /*#__PURE__*/jsx(CloseIconContainer, {
      children: /*#__PURE__*/jsx(LinkButton, {
        label: t("ContentChipBase.closeButtonLabel"),
        variant: "secondary",
        hideLabel: true,
        iconSize: 16,
        onClick: onClose,
        startAdornment: IconClose
      })
    })]
  }));
});
ContentChipBase.displayName = "ContentChipBase";

export { ContentChipBase };
